import React from "react";
import { Input, Spacer, VDBText } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import { messageType } from "@components/molecules/InputPopUp/types";
import Popup from "@components/molecules/PopUp";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { buyRequestSchema } from "./requestSchema";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  useOrderActions,
  useUpdateRequestHistory,
  useWholeRequestOrderActions,
} from "@apis/requestHistory";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import { ActiveClickTypes } from "../types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { productNameToTokenCategory } from "@utils/index";
import { useQueryClient } from "@tanstack/react-query";
import { useSelectedItems } from "../../../contexts/SelectedItemsContext";

type RequestConfirmPopupType = {
  stockItemRequest: any;
  activeClick: "acceptQuote" | "accept" | "decline" | "withdraw";
  onPopupShowChange: (status: boolean) => void;
  onSubmitChange: (data: any) => void;
  orderLineActions?: boolean;
};

const RequestConfirmPopup = (props: RequestConfirmPopupType) => {
  const {
    stockItemRequest,
    activeClick,
    onPopupShowChange,
    onSubmitChange,
    orderLineActions = false,
  } = props;
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const activeProductThemeCategory = productNameToTokenCategory(activeProduct);
  const updateStatus = useUpdateRequestHistory(stockItemRequest.id);
  const orderActions = useOrderActions(String(stockItemRequest?.id ?? ""));
  const wholeRequestActions = useWholeRequestOrderActions(
    String(stockItemRequest?.id ?? "")
  );
  const queryClient = useQueryClient();
  const { selectedItems } = useSelectedItems();
  const isQuoteAcceptOnBehalf =
    stockItemRequest?.view_type === "buyer_view" &&
    (orderLineActions || !stockItemRequest?.quote_details?.is_user_registered);

  const { control, getValues } = useForm({
    resolver: yupResolver(buyRequestSchema()),
  });

  enum RequestStatus {
    CANCEL = "3",
    DECLINE = "7",
    WITHDRAW = "6",
    ACCEPT = "5",
  }

  const popupStyles = {
    decline: {
      type: messageType.Error,
      title: t("requestHistoryDetail.decline.requestPopup.title.text"),
      bodyTitle: t(
        "requestHistoryDetail.decline.requestPopup.description.text"
      ),
      showComments: true,
      primaryButtonText: t(
        "requestHistoryDetail.decline.requestPopup.primaryButton.text"
      ),
      primaryButtonOnPress: () => {
        if (orderLineActions) {
          submitWholeRequestAction("decline");
          return;
        }
        updateRequestStatus(RequestStatus.DECLINE);
      },
    },
    withdraw: {
      type: messageType.Error,
      title: t("requestHistoryDetail.withdraw.requestPopup.title.text"),
      bodyTitle: t(
        "requestHistoryDetail.withdraw.requestPopup.description.text"
      ),
      showComments: true,
      primaryButtonText: t(
        "requestHistoryDetail.withdraw.requestPopup.primaryButton.text"
      ),
      primaryButtonOnPress: () => {
        if (orderLineActions) {
          submitWholeRequestAction("withdraw");
          return;
        }
        updateRequestStatus(RequestStatus.WITHDRAW);
      },
    },
    accept: {
      type: messageType.Success,
      title: t("requestHistoryDetail.accept.requestPopup.title.text"),
      bodyTitle: t("requestHistoryDetail.accept.requestPopup.description.text"),
      showComments: false,
      primaryButtonText: t(
        "requestHistoryDetail.accept.requestPopup.primaryButton.text"
      ),
      primaryButtonOnPress: () => {
        updateRequestStatus(RequestStatus.ACCEPT);
      },
    },
    acceptQuote: {
      type: messageType.Success,
      title: t(
        isQuoteAcceptOnBehalf
          ? "requestHistoryDetail.acceptBehalf.quotePopup.title.text"
          : "requestHistoryDetail.accept.quotePopup.title.text",
        isQuoteAcceptOnBehalf ? "Accept on Behalf" : "Accept Quote"
      ),
      bodyTitle: t(
        isQuoteAcceptOnBehalf
          ? "requestHistoryDetail.acceptBehalf.quotePopup.description.text"
          : "requestHistoryDetail.accept.quotePopup.confirmDescription.text",
        isQuoteAcceptOnBehalf
          ? "Are you sure you want to accept the selected items on behalf of the recipient? Any items left unselected will be closed out automatically."
          : "Are you sure you want to accept the selected items? Any items left unselected will be closed out automatically."
      ),
      showComments: false,
      primaryButtonText: t(
        "requestHistoryDetail.accept.requestPopup.primaryButton.text"
      ),
      primaryButtonOnPress: () => {
        if (orderLineActions) {
          submitOrderLineAction("accept");
          return;
        }
        updateRequestStatus(RequestStatus.ACCEPT);
      },
    },
  };

  const submitWholeRequestAction = (status: "withdraw" | "decline") => {
    if (!stockItemRequest?.id) return;
    const actionComment = getValues("comments")?.trim();

    wholeRequestActions.mutate(
      {
        status,
        ...(actionComment ? { action_comment: actionComment } : {}),
      },
      {
        onSuccess() {
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request_detail"] })
            .then();
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request"] })
            .then();
          onPopupShowChange(false);
          showToast(
            undefined,
            status === "decline"
              ? t("requestHistoryDetail.decline.requestPopup.successToast.text")
              : t(
                  "requestHistoryDetail.withdraw.requestPopup.successToast.text"
                ),
            ToastTypes.ERROR,
            true,
            true,
            0,
            () => null,
            false
          );
          onSubmitChange(undefined);
        },
      }
    );
  };

  const submitOrderLineAction = (status: "accept") => {
    const orderedItemIds = (selectedItems ?? [])
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id));
    if (!stockItemRequest?.id || orderedItemIds.length === 0) return;

    orderActions.mutate(
      { status, ordered_item_ids: orderedItemIds },
      {
        onSuccess() {
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request_detail"] })
            .then();
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request"] })
            .then();
          onPopupShowChange(false);
          showToast(
            undefined,
            status === "accept"
              ? t("requestHistoryDetail.accept.requestPopup.successToast.text")
              : status === "decline"
              ? t("requestHistoryDetail.decline.requestPopup.successToast.text")
              : t(
                  "requestHistoryDetail.withdraw.requestPopup.successToast.text"
                ),
            status === "accept" ? ToastTypes.SUCCESS : ToastTypes.ERROR,
            true,
            true,
            0,
            () => null,
            false
          );
          onSubmitChange(undefined);
        },
      }
    );
  };

  const updateRequestStatus = (status: string) => {
    let payload: any = {
      status: status,
    };
    if (getValues("comments")) payload.notes = getValues("comments");
    updateStatus.mutate(
      {
        stock_item_request: payload,
        ...(selectedItems?.length > 0
          ? { stock_item_request_ids: selectedItems }
          : {}),
      },
      {
        onSuccess(successData: any) {
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request_detail"] })
            .then();
          queryClient
            .invalidateQueries({ queryKey: ["stock_item_request"] })
            .then();
          onPopupShowChange(false);

          if (
            status === RequestStatus.ACCEPT ||
            status === ActiveClickTypes.ACCEPTQUOTE
          )
            showToast(
              undefined,
              t("requestHistoryDetail.accept.requestPopup.successToast.text"),
              ToastTypes.SUCCESS,
              true,
              true,
              0,
              () => null,
              false
            );
          else if (status === RequestStatus.DECLINE)
            showToast(
              undefined,
              t("requestHistoryDetail.decline.requestPopup.successToast.text"),
              ToastTypes.ERROR,
              true,
              true,
              0,
              () => null,
              false
            );
          else if (status === RequestStatus.WITHDRAW)
            showToast(
              undefined,
              t("requestHistoryDetail.withdraw.requestPopup.successToast.text"),
              ToastTypes.ERROR,
              true,
              true,
              0,
              () => null,
              false
            );
          onSubmitChange(successData.data?.data?.stock_item_request);
        },
      }
    );
  };

  return (
    <Popup
      type={popupStyles[activeClick].type}
      styleKey="Body-R"
      title={popupStyles[activeClick].title}
      buttons={[
        {
          type: ButtonType.primary,
          title: popupStyles[activeClick].primaryButtonText,
          onPressAction: popupStyles[activeClick].primaryButtonOnPress,
          loading: !!(
            updateStatus.isLoading ||
            orderActions.isLoading ||
            wholeRequestActions.isLoading
          ),
          disabled: !!(
            updateStatus.isLoading ||
            orderActions.isLoading ||
            wholeRequestActions.isLoading
          ),
        },
        {
          type: ButtonType.secondary,
          title: t("requestHistoryDetail.requestPopup.cancelButton.text"),
          category: activeProductThemeCategory,
          onPressAction: () => {
            onPopupShowChange(false);
          },
        },
      ]}
      hasChildren={true}
    >
      {popupStyles[activeClick].bodyTitle && (
        <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
          {popupStyles[activeClick].bodyTitle}
        </VDBText>
      )}
      <Spacer size={SpaceType.Basic} />
      {popupStyles[activeClick].showComments && (
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            formState: { errors },
          }) => (
            <Input
              inputLabel={t(
                "requestHistoryDetail.accept.requestPopup.inputLabel.text"
              )}
              placeholder={t(
                "requestHistoryDetail.accept.requestPopup.inputLabel.text"
              )}
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              error={errors?.comments?.message}
              multiline={true}
            />
          )}
          name="comments"
        />
      )}
    </Popup>
  );
};

export default RequestConfirmPopup;
